/**
 * Карточка «Темы отклонений» (US-04.9.1 FR-2.2) — таблица «тема · бар ·
 * доля · кол-во сёл» на всю ширину раздела (бары читаются только широко).
 *
 * Знаменатель темы — сколько сёл ответило именно по ней (графы
 * «Мониторинга СНП» заполнены неравномерно: автобусное сообщение — 45 сёл
 * из 585), поэтому доля считается от `answered`, а подпись «N из M»
 * показывается в ячейке количества, когда знаменатель темы отличается от
 * общего числа сёл территории.
 */
import {
  AlertCircle,
  Baby,
  Bus,
  Cross,
  Droplet,
  Droplets,
  GraduationCap,
  HeartPulse,
  PlugZap,
  Route,
  Stethoscope,
  Truck,
  Wifi,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  BarCell,
  CountCell,
  HeadCell,
  NoData,
  ROW,
  ShareCell,
  TextCell,
  WidgetCard,
  sharePct,
} from './snpTable';
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import type { SnpTheme } from '../../../types/snpNeeds';

/** Иконка темы отклонения; незнакомый ключ — нейтральный значок. */
const THEME_ICON: Record<string, LucideIcon> = {
  linesWear: Zap,
  substationsWear: PlugZap,
  innerRoads: Route,
  busService: Bus,
  centralWater: Droplet,
  waterQuality: Droplets,
  deliveredWater: Truck,
  internet: Wifi,
  schoolPlaces: GraduationCap,
  kindergartenPlaces: Baby,
  doctorsDeficit: Stethoscope,
  nursesDeficit: HeartPulse,
  noHealthFacility: Cross,
};

interface SnpThemesProps {
  themes: SnpTheme[];
  totalVillages: number | null;
}

export const SnpThemes = ({ themes, totalVillages }: SnpThemesProps) => {
  const { t } = useTranslation();

  const rows = themes
    .filter((theme) => theme.villages !== null)
    .sort((a, b) => (b.villages ?? 0) - (a.villages ?? 0));

  return (
    <WidgetCard
      testId="snp-themes"
      title={t('analytics.snpNeeds.plashka.themes')}
    >
      {rows.length === 0 ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.theme')}</HeadCell>
              <HeadCell />
              <HeadCell num>{t('analytics.snpNeeds.columns.share')}</HeadCell>
              <HeadCell num>
                {t('analytics.snpNeeds.columns.villagesCount')}
              </HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((theme) => {
              const answered = theme.answered ?? totalVillages;
              const pct = sharePct(theme.villages, answered);
              const Icon = THEME_ICON[theme.key] ?? AlertCircle;
              return (
                <TableRow
                  key={theme.key}
                  className={ROW}
                  data-testid={`snp-theme-${theme.key}`}
                >
                  <TextCell>
                    <span className="flex items-center gap-2.5">
                      <span
                        className="flex size-6 shrink-0 items-center justify-center rounded bg-brand-50 text-brand-600"
                        aria-hidden="true"
                      >
                        <Icon className="size-3.5" strokeWidth={1.8} />
                      </span>
                      <span className="leading-snug">
                        {t(`analytics.snpNeeds.topics.${theme.key}`, {
                          defaultValue: theme.key,
                        })}
                      </span>
                    </span>
                  </TextCell>
                  <BarCell pct={pct} />
                  <ShareCell pct={pct} />
                  {/* Знаменатель темы свой; совпадает с числом сёл
                    территории — печатается одно число (см. CountCell). */}
                  <CountCell
                    count={{
                      present: theme.villages,
                      answered: theme.answered,
                    }}
                    base={totalVillages}
                  />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};
